import { z } from "zod";
import { eventTypeSchema } from "./events.js";
import { formationSchemeSchema, lineupSlotSchema, SLOT_COUNT } from "./lineup.js";
import { varDecisionSchema, varSubjectSchema } from "./replicants.js";
import { matchPhaseSchema, statsFieldsSchema } from "./state.js";

export const addEventCommandSchema = z.object({
  type: z.literal("event.add"),
  payload: z.object({
    eventType: eventTypeSchema,
    teamId: z.string(),
    playerId: z.string(),
    assistPlayerId: z.string().nullable().optional(),
    minute: z.number().int().min(0).max(130),
    second: z.number().int().min(0).max(59),
    data: z.record(z.unknown()).optional(),
  }),
});

export const deleteEventCommandSchema = z.object({
  type: z.literal("event.delete"),
  payload: z.object({ eventId: z.string() }),
});

export const updateTimerCommandSchema = z.object({
  type: z.literal("timer.update"),
  payload: z.object({
    minute: z.number().int().min(0).max(130),
    second: z.number().int().min(0).max(59),
    phase: matchPhaseSchema,
    additionalTime: z.number().int().nonnegative().default(0),
    running: z.boolean(),
  }),
});

export const updateStatsCommandSchema = z.object({
  type: z.literal("stats.update"),
  payload: z.object({
    side: z.enum(["home", "away"]),
    stats: statsFieldsSchema.partial(),
  }),
});

export const showOverlayCommandSchema = z.object({
  type: z.literal("overlay.show"),
  payload: z.object({
    mode: z.string(),
    data: z.record(z.unknown()).optional(),
  }),
});

export const hideOverlayCommandSchema = z.object({
  type: z.literal("overlay.hide"),
  payload: z.object({ mode: z.string() }),
});

// VAR — opens the review overlay in "checking" state.
export const showVarCommandSchema = z.object({
  type: z.literal("var.show"),
  payload: z.object({
    subject: varSubjectSchema,
    subjectPlayerId: z.string().nullable().optional(),
    subjectEventId: z.string().nullable().optional(),
  }),
});

// VAR — moves the overlay to "decided" with a verdict.
export const decideVarCommandSchema = z.object({
  type: z.literal("var.decide"),
  payload: z.object({
    decision: varDecisionSchema,
  }),
});

// VAR — hides the overlay and clears state.
export const hideVarCommandSchema = z.object({
  type: z.literal("var.hide"),
  payload: z.object({}).default({}),
});

// Lineup — opens the formation overlay with side, scheme, and 11 slots.
export const showLineupCommandSchema = z.object({
  type: z.literal("lineup.show"),
  payload: z.object({
    side: z.enum(["home", "away"]),
    scheme: formationSchemeSchema,
    slots: z.array(lineupSlotSchema).length(SLOT_COUNT),
  }),
});

// Lineup — patch a single slot in-place (assign or clear).
export const updateLineupSlotCommandSchema = z.object({
  type: z.literal("lineup.update"),
  payload: z.object({
    slotIndex: z.number().int().min(0).max(SLOT_COUNT - 1),
    slot: lineupSlotSchema,
  }),
});

// Lineup — change the active scheme without losing assigned players where
// the new scheme has the same slot index.
export const setLineupSchemeCommandSchema = z.object({
  type: z.literal("lineup.setScheme"),
  payload: z.object({ scheme: formationSchemeSchema }),
});

// Lineup — hide the overlay and clear state.
export const hideLineupCommandSchema = z.object({
  type: z.literal("lineup.hide"),
  payload: z.object({}).default({}),
});

export const commandSchema = z.discriminatedUnion("type", [
  addEventCommandSchema,
  deleteEventCommandSchema,
  updateTimerCommandSchema,
  updateStatsCommandSchema,
  showOverlayCommandSchema,
  hideOverlayCommandSchema,
  showVarCommandSchema,
  decideVarCommandSchema,
  hideVarCommandSchema,
  showLineupCommandSchema,
  updateLineupSlotCommandSchema,
  setLineupSchemeCommandSchema,
  hideLineupCommandSchema,
]);
export type Command = z.infer<typeof commandSchema>;

// NodeCG transport: the control panel sends `cmd` messages with
// { slug, command }. The extension dispatches to the right project.
export const commandEnvelopeSchema = z.object({
  slug: z.string().min(1).max(64),
  command: commandSchema,
});
export type CommandEnvelope = z.infer<typeof commandEnvelopeSchema>;
